<script setup lang="ts">
import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import type { Ref } from "vue";
import DialogTitle from "@verbleif/lib/src/components/Dialog/DialogTitle.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { NotificationChannelEnum } from "@verbleif/lib/src/composables/Api/Type/NotificationRule/notificationRule.interface";
import { tryUseDialogLayer } from "@verbleif/lib/src/composables/Dialog/useDialogLayer";
import { ScrollDirection, useLazyLoadScroll } from "@verbleif/lib/src/composables/Scroll/useLazyLoadScroll";
import moment from "moment";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { truncateText } from "../../truncateText";
import { inboxNotificationRoute, parseInboxNotificationLink } from "./inboxNotificationLink";
import { useInboxNotifications } from "./useInboxNotifications";

const { embedded = false } = defineProps<{
  /** Rendered inside the phone account sheet instead of a dialog: the sheet shows the title and sets the height. */
  embedded?: boolean
}>();

const { t } = useI18n();
const router = useRouter();
const container: Ref<HTMLElement | null> = ref(null);
const layer = tryUseDialogLayer();

const {
  isAllRead,
  items,
  loading,
  searchable,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  markAsRead,
  markOneAsRead,
  hide,
  hasError,
  hasNotifications,
  refetch,
} = useInboxNotifications();

const TRANSLATION_KEY = "settings.notifications.v2.inbox";

function fromNow(createdAt: string) {
  const currentTime = moment();
  const createdAtTime = moment(createdAt);
  const diffInSeconds = currentTime.diff(createdAtTime, "seconds");

  if (diffInSeconds < 60) {
    return t(`${TRANSLATION_KEY}.just_now`);
  } else {
    return createdAtTime.from(currentTime);
  }
}

async function notificationLocation(notification: UserNotificationHydraItem) {
  const target = parseInboxNotificationLink(notification.link);
  if (!target) {
    toast.error(t(`${TRANSLATION_KEY}.no_path`));
    return;
  }

  await markOneAsRead(notification);
  // Close this dialog first: a dialog opened by the route (the task, the report) would otherwise be stacked on top of
  // it and close with it, which drops the item from the route again.
  await layer?.close();
  await router.push(inboxNotificationRoute(target));
}

async function handleMarkAsRead(notification: UserNotificationHydraItem, event: Event) {
  event.stopPropagation();
  await markOneAsRead(notification);
}

async function handleHide(notification: UserNotificationHydraItem, event: Event) {
  event.stopPropagation();
  await hide(notification);
}

// The interrupting channels an item went out on; the inbox itself is not a channel of the item.
const CHANNEL_DISPLAY: Partial<Record<NotificationChannelEnum, { icon: IconName, labelKey: string }>> = {
  [NotificationChannelEnum.EMAIL]: { icon: "mail", labelKey: "settings.notifications.v2.channels.email" },
  [NotificationChannelEnum.WEB_PUSH]: { icon: "monitor", labelKey: "settings.notifications.v2.channels.web_push" },
  [NotificationChannelEnum.MOBILE_APP]: { icon: "smartphone", labelKey: "settings.notifications.v2.channels.mobile_app" },
};

function sentChannels(notification: UserNotificationHydraItem) {
  return notification.channels.flatMap((channel) => {
    const display = CHANNEL_DISPLAY[channel];
    return display ? [{ channel, icon: display.icon, label: t(display.labelKey) }] : [];
  });
}

function sentViaLabel(notification: UserNotificationHydraItem): string {
  return t(`${TRANSLATION_KEY}.sent_via`, { channels: sentChannels(notification).map(entry => entry.label).join(", ") });
}

useLazyLoadScroll(
  container,
  () => fetchNextPage(),
  hasNextPage,
  isFetchingNextPage,
  ScrollDirection.BOTTOM,
);

onMounted(() => {
  void refetch();
});
</script>

<template>
  <div class="h-full">
    <div class="flex w-full flex-1 flex-col leading-5 text-gray-800 dark:text-white" :class="embedded ? 'h-full' : 'h-[600px] max-h-[75vh]'">
      <div v-if="!embedded" class="flex items-start justify-between gap-3">
        <DialogTitle :reserve-close="false">
          {{ t(`${TRANSLATION_KEY}.title`) }}
        </DialogTitle>
        <button
          type="button"
          class="shrink-0 rounded-xs pt-5 pr-14 text-sm text-primary-600 hover:text-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden disabled:pointer-events-none disabled:text-gray-300 dark:disabled:text-dark-500"
          :disabled="!hasNotifications || isAllRead"
          @click="markAsRead()"
        >
          {{ t(`${TRANSLATION_KEY}.mark_all_read`) }}
        </button>
      </div>
      <div v-if="embedded" class="flex justify-end px-5 pb-2 shrink-0">
        <button
          type="button"
          class="text-sm"
          :class="hasNotifications && !isAllRead ? 'text-primary-600 active:text-primary-700' : 'text-gray-300 pointer-events-none'"
          @click="markAsRead()"
        >
          {{ t(`${TRANSLATION_KEY}.mark_all_read`) }}
        </button>
      </div>
      <div class="px-5 shrink-0">
        <VInput
          v-model="searchable.searchValue.value"
          clearable
          icon-left="search"
          :placeholder="t(`${TRANSLATION_KEY}.search`)"
          :aria-label="t(`${TRANSLATION_KEY}.search`)"
        />
      </div>
      <div v-if="hasError" class="flex flex-1 flex-col items-center justify-center overflow-hidden p-8 text-center" role="alert">
        <VIcon name="triangle-alert" class="mb-4 text-4xl text-danger-500" aria-hidden="true" />
        <div class="text-gray-600 dark:text-gray-400">
          {{ t(`${TRANSLATION_KEY}.error_loading`) }}
        </div>
        <VButton variant="secondary" class="mt-4" @click="refetch">
          {{ t(`${TRANSLATION_KEY}.try_again`) }}
        </VButton>
      </div>
      <div
        v-else-if="!loading && !hasNotifications && !searchable.searchValue.value"
        class="flex flex-1 flex-col items-center justify-center overflow-hidden p-8 text-center"
      >
        <VIcon name="inbox" class="mb-4 text-4xl text-gray-400" aria-hidden="true" />
        <div class="text-gray-600 dark:text-gray-400">
          {{ t(`${TRANSLATION_KEY}.no_notifications`) }}
        </div>
      </div>
      <div
        v-else-if="!loading && !hasNotifications && searchable.searchValue.value"
        class="flex flex-1 flex-col items-center justify-center overflow-hidden p-8 text-center"
      >
        <VIcon name="search" class="mb-4 text-4xl text-gray-400" aria-hidden="true" />
        <div class="text-gray-600 dark:text-gray-400">
          {{ t(`${TRANSLATION_KEY}.no_search_results`) }}
        </div>
      </div>
      <div v-else class="flex min-h-0 flex-1 overflow-hidden">
        <ul ref="container" class="m-0 mt-3 flex w-full list-none flex-col overflow-y-auto p-0 text-gray-800 dark:text-white">
          <li
            v-for="(notification, index) in items"
            :key="'skeleton' in notification ? `skeleton-${index}` : notification.id"
            class="group/notification relative hover:bg-gray-100 dark:hover:bg-dark-800"
          >
            <template v-if="'skeleton' in notification">
              <div class="flex rounded-lg py-3 pr-3 pl-2" aria-hidden="true">
                <div class="flex h-6 w-5 items-center justify-center">
                  <VSkeleton
                    v-if="index % 2 === 0"
                    class="mb-1"
                    :dynamic-width="false"
                    :width="8"
                    :height="8"
                    width-unit="px"
                    height-unit="px"
                  />
                </div>

                <div class="flex w-full flex-col">
                  <div class="flex justify-between">
                    <VSkeleton
                      class="mb-2"
                      :width="100"
                      :height="16"
                      :dynamic-width="false"
                      width-unit="px"
                      height-unit="px"
                    />
                    <VSkeleton
                      class="mb-1"
                      :width="100"
                      :height="12"
                      :dynamic-width="false"
                      width-unit="px"
                      height-unit="px"
                    />
                  </div>
                  <div class="flex flex-col">
                    <VSkeleton
                      class="mb-1"
                      :width="200"
                      :height="16"
                      :dynamic-width="false"
                      width-unit="px"
                      height-unit="px"
                    />
                    <VSkeleton
                      class="mb-1"
                      :width="150"
                      :height="16"
                      :dynamic-width="false"
                      width-unit="px"
                      height-unit="px"
                    />
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="mx-5">
                <VDivider no-margins />
              </div>
              <button
                v-close-popper.all="notification.link"
                type="button"
                class="flex w-full cursor-pointer rounded-lg py-3 pr-16 text-left focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden focus-visible:ring-inset"
                @click="notificationLocation(notification)"
              >
                <span class="flex h-4 w-5 shrink-0 items-center justify-center">
                  <span v-if="!notification.readAt" class="h-2 w-2 rounded-full bg-primary-600" aria-hidden="true" />
                  <span v-if="!notification.readAt" class="sr-only">{{ t(`${TRANSLATION_KEY}.unread`) }}</span>
                </span>
                <span class="flex min-w-0 grow flex-col">
                  <span class="flex justify-between gap-3">
                    <span :class="notification.readAt ? '' : 'font-bold'" class="mb-1 truncate text-sm leading-4 dark:text-white">
                      {{ notification.title }}
                    </span>
                    <span v-if="notification.listedAt ?? notification.createdAt" class="shrink-0 text-xs text-gray-400 dark:text-gray-300">
                      {{ fromNow(notification.listedAt ?? notification.createdAt) }}
                    </span>
                  </span>
                  <span class="text-sm leading-4 font-normal text-gray-500 dark:text-gray-300">
                    {{ truncateText(notification.message ?? '', 65) }}
                  </span>
                  <span
                    v-if="sentChannels(notification).length"
                    class="mt-1.5 flex items-center gap-1.5 text-gray-400 dark:text-gray-400"
                    data-testid="inbox-sent-channels"
                    :title="sentViaLabel(notification)"
                  >
                    <VIcon
                      v-for="entry in sentChannels(notification)"
                      :key="entry.channel"
                      :name="entry.icon"
                      class="size-3"
                      aria-hidden="true"
                    />
                    <span class="sr-only">{{ sentViaLabel(notification) }}</span>
                  </span>
                </span>
              </button>
              <!-- Shown on hover or focus; a touch screen has neither, so there they are always shown. -->
              <div class="absolute right-3 bottom-3 hidden items-center gap-2 group-focus-within/notification:flex group-hover/notification:flex pointer-coarse:flex" data-testid="inbox-row-actions">
                <VTooltip v-if="!notification.readAt" theme="verbleif-tooltip-dark" placement="top">
                  <template #content>
                    {{ t(`${TRANSLATION_KEY}.mark_as_read`) }}
                  </template>
                  <template #reference>
                    <button
                      type="button"
                      class="flex rounded-xs text-sm text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden"
                      :aria-label="t(`${TRANSLATION_KEY}.mark_as_read`)"
                      @click="handleMarkAsRead(notification, $event)"
                    >
                      <VIcon name="circle-check" aria-hidden="true" />
                    </button>
                  </template>
                </VTooltip>
                <VTooltip theme="verbleif-tooltip-dark" placement="top">
                  <template #content>
                    {{ t(`${TRANSLATION_KEY}.hide`) }}
                  </template>
                  <template #reference>
                    <button
                      type="button"
                      class="flex rounded-xs text-sm text-gray-500 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden dark:text-gray-300 dark:hover:text-white"
                      :aria-label="t(`${TRANSLATION_KEY}.hide`)"
                      data-testid="inbox-hide"
                      @click="handleHide(notification, $event)"
                    >
                      <VIcon name="eye-off" aria-hidden="true" />
                    </button>
                  </template>
                </VTooltip>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
